import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Button,
  TextField,
  IconButton,
  Box,
  Select,
  MenuItem,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { styled } from '@mui/material/styles';
// components
import Iconify from "../../components/Iconify";
import DIOText from '../../components/DIOText';
import SvgIconStyle from '../../components/SvgIconStyle';
import {
  NewCoffeeChatStepper,
} from './coffeechat';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from "../../hooks/useResponsive";
// utils
import axios from '../../utils/axios';

const InfoText = styled('span')(({ theme }) => ({
  color: theme.palette.info.main,
}));

// ----------------------------------------------------------------------

export default function CoffeeChatDialogButton({ profile, reload, freeSolo=false, inDropdown=false }) {
  const smUp = useResponsive("up", "sm");
  const { user } = useAuth();
  const [isSubmit, setIsSubmit] = useState(false);
  const [step, setStep] = useState(0);  // 0, 1, 2
  const [open, setOpen] = useState(false);
  const disabled = profile?.coffee_chat_ing;

  const messageStep = step === 0;
  const termStep = step === 1;
  const done = step === 2;

  const [roleName, setRoleName] = useState('');
  const [message, setMessage] = useState('');
  const [salaryRange, setSalaryRange] = useState('');
  const [title, setTitle] = useState('');
  const [stockOption, setStockOption] = useState('');
  const [remoteWork, setRemoteWork] = useState('');

  const reset = () => {
    setMessage('');
    setSalaryRange('');
    setTitle('');
    setStockOption('');
    setRemoteWork('');
  };

  const { enqueueSnackbar } = useSnackbar();

  const closeReloadDialog = () => {
    setOpen(false);
    reload();
  }

  const requestCoffeeChat = async () => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    try {
      const crewId = profile?.id;
      const response = await axios.post(`/api/company/${companyId}/fliers/${crewId}/coffee-draft/`);
      setOpen(true);
      setMessage(response.data.draft);
      setIsSubmit(false);
    } catch (error) {
      setIsSubmit(false);
      if (error.response && error.response.status === 400) {
        enqueueSnackbar(error.response.data.message, { variant: 'error' });
      } else {
        enqueueSnackbar("데이터를 불러오는데 실패하였습니다. 관리자에 문의해주세요.", { variant: 'error' });
      }
    }
  };

  const goToTermStep = async () => {
    setStep(1);
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    try {
      const crewId = profile?.id;
      const response = await axios.get(`/api/company/${companyId}/fliers/${crewId}/coffee-group/`);
      setRoleName(response?.data?.role_name || `${profile?.card_display_name || ''}` || `${profile?.main_role || ''} ${profile?.position_display || ''}` || '');
      setSalaryRange(response?.data?.salary_range || '');
      setTitle(response?.data?.title || '');
      setStockOption(response?.data?.stock_option || '');
      setRemoteWork(response?.data?.remote_work || '');
      setIsSubmit(false);
    } catch (error) {
      setIsSubmit(false);
      if (error.response && error.response.status === 400) {
        enqueueSnackbar(error.response.data.message, { variant: 'error' });
      } else {
        enqueueSnackbar("데이터를 불러오는데 실패하였습니다. 관리자에 문의해주세요.", { variant: 'error' });
      }
    }
  }

  useEffect(() => {
    if (!open) {
      setStep(0);
      reset();
    }
  }, [open]);

  const submit = async () => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    try {
      const postData = {
        message: message,
        offer_position: roleName,
        salary_range: salaryRange,
        title: title,
        stock_option: stockOption,
        remote_work: remoteWork,
      };
      const crewId = profile?.id;
      await axios.post(`/api/company/${companyId}/fliers/${crewId}/coffee/`, postData);
      enqueueSnackbar('커피챗 제안이 완료되었습니다.', { variant: 'success' });
      setIsSubmit(false);
      setStep(2);
    } catch (error) {
      setIsSubmit(false);
      if (error.response && error.response.status === 400) {
        enqueueSnackbar(error.response.data.message, { variant: 'error' });
      } else {
        enqueueSnackbar("저장에 실패하였습니다. 관리자에 문의해주세요.", { variant: 'error' });
      }
    }
  };

  const stopPropagationForTab = (e) => {
    if (e.key === "Tab" || e.key === "d" || e.key === "D") {
      // 최초 입력할때 d 를 누르면 [DIO에게 맡기기] 메뉴 아이템으로 인풋 포커스가 이동하는걸 방지하기 위해 추가함
      e.stopPropagation();
    }
  };

  return (
    <>
    {inDropdown ? (
      <MenuItem
        onClick={requestCoffeeChat}
        disableRipple
        disabled={disabled}
        sx={{ m: 1, borderRadius: .5 }}
      >
        <SvgIconStyle src={`/icons/icon-pencil-mono.svg`} sx={{ mr: 2, width: 20, height: 20 }} />
        <Typography variant={"subtitle2"}>
          {disabled ? "커피챗 제안 완료" : "직접 제안하기"}
        </Typography>
      </MenuItem>
    ) : (
      <LoadingButton
        sx={{ px: 2 }}
        fullWidth={freeSolo && !smUp}
        variant="contained"
        size={freeSolo ? "large" : "medium"}
        onClick={requestCoffeeChat}
        disableRipple
        disabled={disabled}
        loading={isSubmit}
        startIcon={freeSolo && <SvgIconStyle src={`/icons/icon-user-check-mono.svg`} sx={{ width: 24, height: 24 }} />}
      >
        {disabled ? "커피챗 제안 완료" : freeSolo ? "이 크루에게 커피챗 제안하기" : "커피챗 제안하기"}
      </LoadingButton>
    )}
    <Dialog
      open={open}
      onClose={closeReloadDialog}
      fullScreen={!smUp && (step < 2)}
      fullWidth
      maxWidth="sm"
      sx={{ p: 0 }}
      onKeyDown={stopPropagationForTab}
    >
      {done ? (
        <DialogContent
        sx={{ p: { xs: 3, sm: 4 } }}
      >
        <Iconify icon={"eva:info-fill"} sx={{ width: 24, height: 24, color: 'info.main' }} />
        <Stack direction="row" justifyContent={"space-between"} alignItems={"center"} sx={{ mb: 2, mt: 0 }}>
          <Typography variant="h5">
            커피챗 제안을 완료했어요.
          </Typography>
        </Stack>
        <Typography variant="body1">
          크루가 커피챗을 수락하면 <DIOText /> 에서 연락드릴게요. 내 인재풀 페이지에서 진행과정을 확인할 수 있어요.
        </Typography>
      </DialogContent>
      ) : (
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Box>
          <IconButton color="inherit" edge="start" sx={{ ...(step === 0) && {visibility: 'hidden' } }} onClick={() => setStep(0)}>
            <Iconify icon="ic:chevron-left" sx={{ width: smUp ? 32 : 24 , height: smUp ? 32 : 24, color: (theme) => theme.palette.primary.main }} />
          </IconButton>
          </Box>
          <NewCoffeeChatStepper step={step} />
          <Box sx={{ visibility: "hidden" }}>
            <IconButton color="inherit" edge="end" disabled>
              <Iconify icon="ic:chevron-right" sx={{ width: smUp ? 32 : 24 , height: smUp ? 32 : 24 }} />
            </IconButton>
          </Box>
        </Stack>

        {step === 0 && (
        <>
        <Stack sx={{ my: 3 }} spacing={1}>
          <Typography variant="h5">
            <InfoText>커피챗 제안 메시지</InfoText>를 작성해주세요.
          </Typography>
          <Typography variant="body2">
            크루는 커피챗 제안 메시지를 확인하고, 커피챗을 수락 또는 거절합니다.<br />
            커피챗 시간은 크루가 커피챗을 수락하면 정할 수 있어요.
          </Typography>
        </Stack>

        <TextField
          multiline
          fullWidth
          minRows={10}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={`크루를 만나고 싶은 이유를 남겨주시면 커피챗이 더 잘 성사됩니다. 시간과 장소는 커피챗 수락 이후에 정하는 것이 효과적입니다.`}
        />

        <Stack sx={{ mt: 2, mb: -1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify icon={"eva:info-fill"} sx={{ width: 20, height: 20, color: 'info.main' }} />
            <Typography variant="body2">
              <DIOText/>에서 입력해둔 샘플 메시지를 활용해보세요.
            </Typography>
          </Stack>
        </Stack>
        </>
        )}

        {step === 1 && (
        <>
        <Stack sx={{ my: 3 }} spacing={1}>
          <Typography variant="h5">
            대략적인 연봉, 근무 조건도 함께 알려주세요.
          </Typography>
          <Typography variant="body2">
            크루의 응답율을 높이기 위해 필수로 입력받고 있습니다.
          </Typography>
        </Stack>

        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              포지션
            </Typography>
            <TextField
              fullWidth
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
            />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2">
              연봉 수준
            </Typography>
            <TextField
              fullWidth
              value={salaryRange}
              onChange={(e) => setSalaryRange(e.target.value)}
              helperText={'예) 직전 연봉의 20% 인상 / 8천만원 ~ 1억 2천만원'}
            />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2">
              직급
            </Typography>
            <TextField
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              helperText={'예) CTO, 테크리드, 백엔드 팀 리드, 시니어 개발자'}
            />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="body2">
              <b>스톡옵션 조건</b> (선택)
            </Typography>
            <TextField
              fullWidth
              value={stockOption}
              onChange={(e) => setStockOption(e.target.value)}
              helperText={'예) 1년 핏을 확인해본 후, n% 까지 지급할 의사가 있음'}
            />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2">
              재택근무 여부
            </Typography>
            <Select
              fullWidth
              displayEmpty
              value={remoteWork}
              onChange={(e) => setRemoteWork(e.target.value)}
              
            >
              <MenuItem value={"전면 재택근무"} sx={{ m: 1, borderRadius: 1 }}>
                전면 재택근무
              </MenuItem>
              <MenuItem value={"일부 재택근무 가능(상주+재택근무 혼합)"} sx={{ m: 1, borderRadius: 1 }}>
                일부 재택근무 가능(상주+재택근무 혼합)
              </MenuItem>
              <MenuItem value={"재택근무 없음(전면 상주근무)"} sx={{ m: 1, borderRadius: 1 }}>
                재택근무 없음(전면 상주근무)
              </MenuItem>
            </Select>
            <Typography variant="caption" sx={{ pl: "14px", color: "grey.600" }}>
              재택근무는 이직 제안시 어필 포인트가 될 수 있습니다.
            </Typography>
          </Stack>
        </Stack>
        </>
        )}

      </DialogContent>
      )}
      <DialogActions
        sx={{ m: { xs: 0, sm: 1 }, pt: "0!important" }}
      >
        <Stack direction={smUp ? "row" : "column-reverse"} spacing={2} sx={{ width: 1 }} justifyContent="flex-end">
        <LoadingButton
          variant={done ? "contained" : "text"}
          size="large"
          onClick={closeReloadDialog}
          loading={isSubmit}
          fullWidth={!smUp}
          sx={{ px: 4 }}
        >
          {done ? '확인했어요' : '취소'}
        </LoadingButton>
        {messageStep && (
          <Button
            variant="contained"
            size="large"
            fullWidth={!smUp}
            onClick={() => goToTermStep()}
            disabled={!message || message?.length > 3000}
            sx={{ px: 6 }}
          >
            다음
          </Button>
        )}
        {termStep && (
          <LoadingButton
            variant="contained"
            size="large"
            fullWidth={!smUp}
            onClick={submit}
            loading={isSubmit}
            disabled={!salaryRange || !title || !remoteWork}
            sx={{ px: 6 }}
          >
            커피챗 제안하기
          </LoadingButton>
        )}
        </Stack>
      </DialogActions>
    </Dialog>
    </>
  )
};